import React, { useState, useEffect, useCallback } from 'react';
import Layout from '@/components/Layout';
import PollenDotsNav from '@/components/PollenDotsNav';
import { API, useAuth } from '@/App';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Plus, Search, Loader2, Pencil, Trash2, Phone, Building2, MapPin, Check, AlertCircle, ExternalLink, Upload, GitMerge, Sparkles, Megaphone } from 'lucide-react';
import ConfirmDeleteDialog from '@/components/ConfirmDeleteDialog';
import MergeIntoDialog from '@/components/MergeIntoDialog';
import LocationAutocomplete from '@/components/LocationAutocomplete';
import BulkOutreachSheet from '@/components/BulkOutreachSheet';
import { AccommodationTypeTagger, ContactTagger } from '@/components/vendors/InlineTaggers';

const typeColors = { Hotel: 'bg-emerald-100 text-emerald-800', Transport: 'bg-amber-100 text-amber-800', Driver: 'bg-orange-100 text-orange-800', Activity: 'bg-blue-100 text-blue-800', Agent: 'bg-purple-100 text-purple-800', Guide: 'bg-teal-100 text-teal-800' };
const locTypeColors = {
  city: 'bg-teal-100 text-teal-800',
  hotel: 'bg-sky-100 text-sky-800',
  guide: 'bg-rose-100 text-rose-800',
  driver: 'bg-amber-100 text-amber-800',
  activity: 'bg-violet-100 text-violet-800',
  route: 'bg-orange-100 text-orange-800',
  restaurant: 'bg-rose-100 text-rose-800',
  attraction: 'bg-fuchsia-100 text-fuchsia-800',
  pickup_drop: 'bg-orange-100 text-orange-800',
  region: 'bg-indigo-100 text-indigo-800',
};
// Types we actually support as pinnable places. Routes, restaurants, pickup_drop
// and other AI-extracted noise are kept out of the UI so admins only see
// locations that make sense on a map.
const LOCATION_TYPES = ['city', 'hotel', 'guide', 'driver', 'activity', 'enroute_stop'];

const PollenDotsVendors = () => {
  const POLLEN_API = `${API}/pollen`;
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [vendors, setVendors] = useState([]);
  const [locations, setLocations] = useState([]);
  const [mode, setMode] = useState(searchParams.get('mode') === 'locations' ? 'locations' : 'vendors'); // 'vendors' | 'locations'
  const [locTypeFilter, setLocTypeFilter] = useState(searchParams.get('filter') === 'needs-coords' ? '__needs_coords' : '');
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState(searchParams.get('type') || '');
  // Populator-task deep-link state (drives the scope banner). When the
  // user lands here from a "task open" button we pre-filter to that
  // entity_type and surface a banner with the live progress so they
  // know exactly which task they're moving the needle on.
  const taskIdParam = searchParams.get('task') || '';
  const [taskMeta, setTaskMeta] = useState(null);
  const [locationFilter, setLocationFilter] = useState('');
  const [priceMin, setPriceMin] = useState('');
  const [priceMax, setPriceMax] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  // Stage D — bulk outreach sheet (no query attached)
  const [bulkOutreachOpen, setBulkOutreachOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState({ name: '', type: 'Hotel', contact_name: '', phone: '', email: '', location: '' });
  const [editId, setEditId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [mergeSource, setMergeSource] = useState(null);
  const [mergeLocSource, setMergeLocSource] = useState(null);
  // Locations dialog state
  const [locDialogOpen, setLocDialogOpen] = useState(false);
  const [locEditMode, setLocEditMode] = useState(false);
  const [locForm, setLocForm] = useState({ name: '', type: 'city', country: '', state: '', region: '', notes: '' });
  const [locEditId, setLocEditId] = useState(null);
  const [locDeleteId, setLocDeleteId] = useState(null);
  // Bulk pin dialog
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState('');
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  const authFetch = (url, opts = {}) => {
    const h = {}; const t = localStorage.getItem('session_token'); if (t) h['Authorization'] = `Bearer ${t}`;
    return fetch(url, { ...opts, credentials: 'include', headers: { ...opts.headers, ...h } });
  };

  const fetchData = useCallback(async () => {
    try {
      const [vr, lr] = await Promise.all([
        authFetch(`${POLLEN_API}/vendors`),
        authFetch(`${POLLEN_API}/locations?limit=1000`),
      ]);
      if (vr.ok) setVendors(await vr.json());
      if (lr.ok) setLocations(await lr.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // When deep-linked from a populator task, hydrate the scope banner so
  // the assignee sees exactly which task is being progressed and the
  // live X / target counter without leaving this page.
  useEffect(() => {
    if (!taskIdParam) { setTaskMeta(null); return; }
    (async () => {
      try {
        const r = await authFetch(`${POLLEN_API}/listing-assignments?status=`);
        if (!r.ok) return;
        const all = await r.json();
        const t = (all || []).find(x => x.id === taskIdParam);
        if (t) setTaskMeta(t);
      } catch { /* noop */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskIdParam]);

  // Build a quick lookup so we can flag vendors whose location has been
  // pinned (lat/lng resolved) on the public share map. Operators asked
  // for an at-a-glance dot/check on the vendor list rather than having
  // to drill into each card.
  const pinnedKeySet = React.useMemo(() => {
    const set = new Set();
    (locations || []).forEach(l => {
      if (l.lat == null || l.lng == null) return;
      const tp = (l.type || '').toLowerCase();
      const nm = (l.name || '').toLowerCase().trim();
      if (!nm) return;
      set.add(`${nm}|${tp}`);
      set.add(nm);
    });
    return set;
  }, [locations]);
  const isVendorPinned = (v) => {
    if (!v?.name) return false;
    const tp = (v.type || '').toLowerCase();
    const nm = v.name.toLowerCase().trim();
    return pinnedKeySet.has(`${nm}|${tp}`) || pinnedKeySet.has(nm);
  };

  const filtered = vendors.filter(v => {
    const s = search.toLowerCase();
    const matchSearch = !s || v.name?.toLowerCase().includes(s) || v.contact_name?.toLowerCase().includes(s);
    const matchType = !typeFilter || v.type === typeFilter;
    const loc = locationFilter.toLowerCase();
    const matchLoc = !loc || (v.location || '').toLowerCase().includes(loc) || (v.address || '').toLowerCase().includes(loc);
    const roomPrices = (v.rooms || []).map(r => parseFloat(r.price) || 0).filter(p => p > 0);
    const pMin = parseFloat(priceMin);
    const pMax = parseFloat(priceMax);
    let matchPrice = true;
    if (!isNaN(pMin) || !isNaN(pMax)) {
      if (roomPrices.length === 0) matchPrice = false;
      else {
        const minRoom = Math.min(...roomPrices);
        const maxRoom = Math.max(...roomPrices);
        // Vendor qualifies if any room price falls within the filter range
        if (!isNaN(pMin) && maxRoom < pMin) matchPrice = false;
        if (!isNaN(pMax) && minRoom > pMax) matchPrice = false;
      }
    }
    return matchSearch && matchType && matchLoc && matchPrice;
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const url = editMode ? `${POLLEN_API}/vendors/${editId}` : `${POLLEN_API}/vendors`;
    try {
      const res = await authFetch(url, { method: editMode ? 'PUT' : 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form) });
      if (res.ok) { toast.success(editMode ? 'Vendor updated' : 'Vendor added'); setDialogOpen(false); fetchData(); }
      else { const err = await res.json(); toast.error(err.detail || 'Failed'); }
    } catch (e) { toast.error('Error'); }
    setSubmitting(false);
  };

  const handleEdit = (v) => { setForm({ name: v.name, type: v.type || 'Hotel', contact_name: v.contact_name || '', phone: v.phone || '', email: v.email || '', location: v.location || '' }); setEditId(v.id); setEditMode(true); setDialogOpen(true); };
  const handleDelete = async (id) => { await authFetch(`${POLLEN_API}/vendors/${id}`, { method: 'DELETE' }); toast.success('Deleted'); setDeleteId(null); fetchData(); };
  const openAdd = () => { setForm({ name: '', type: 'Hotel', contact_name: '', phone: '', email: '', location: '' }); setEditMode(false); setDialogOpen(true); };

  // ---- Location handlers ----
  const filteredLocations = locations.filter(l => {
    const s = search.toLowerCase();
    const matchSearch = !s || l.name?.toLowerCase().includes(s);
    let matchType = true;
    if (locTypeFilter === '__needs_coords') matchType = !l.verified;
    else if (locTypeFilter) matchType = l.type === locTypeFilter;
    return matchSearch && matchType;
  }).sort((a, b) => {
    // Unverified / missing-coords first so admins can see what still needs pinning.
    const va = a.lat != null ? (a.verified ? 2 : 1) : 0;
    const vb = b.lat != null ? (b.verified ? 2 : 1) : 0;
    if (va !== vb) return va - vb;
    return (a.name || '').localeCompare(b.name || '');
  });
  const handleLocSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const url = locEditMode ? `${POLLEN_API}/locations/${locEditId}` : `${POLLEN_API}/locations`;
    try {
      const res = await authFetch(url, { method: locEditMode ? 'PUT' : 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(locForm) });
      if (res.ok) { toast.success(locEditMode ? 'Location updated' : 'Location added'); setLocDialogOpen(false); fetchData(); }
      else { const err = await res.json(); toast.error(err.detail || 'Failed'); }
    } catch { toast.error('Error'); }
    setSubmitting(false);
  };
  const handleLocEdit = (l) => {
    setLocForm({ name: l.name, type: l.type || 'city', country: l.country || '', state: l.state || '', region: l.region || '', notes: l.notes || '' });
    setLocEditId(l.id); setLocEditMode(true); setLocDialogOpen(true);
  };
  const handleLocDelete = async (id) => { await authFetch(`${POLLEN_API}/locations/${id}`, { method: 'DELETE' }); toast.success('Deleted'); setLocDeleteId(null); fetchData(); };
  const openAddLocation = () => { setLocForm({ name: '', type: 'city', country: '', state: '', region: '', notes: '' }); setLocEditMode(false); setLocDialogOpen(true); };

  const runBulkPin = async () => {
    if (!bulkText.trim()) return;
    setBulkBusy(true); setBulkResult(null);
    try {
      const res = await authFetch(`${POLLEN_API}/locations/bulk-pin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: bulkText }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to bulk-pin');
      setBulkResult(data);
      if (data.pinned?.length) {
        toast.success(`Pinned ${data.pinned.length} location${data.pinned.length === 1 ? '' : 's'} ✓`);
        fetchData();
      } else {
        toast('No matches — double-check the names.');
      }
    } catch (e) {
      toast.error(e.message || 'Bulk pin failed');
    } finally { setBulkBusy(false); }
  };

  if (!['Pollen Dots User','Super Admin','Admin','Operator','Intern'].includes(user?.role)) return <Navigate to="/dashboard" replace />;
  if (loading) return <Layout><div className="flex items-center justify-center h-[60vh]"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div></Layout>;

  return (
    <Layout>
      <div className="space-y-4" data-testid="vendors-list-page">
        <PollenDotsNav />
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <h1 className="text-2xl font-bold tracking-tight">Database</h1>
          <div className="flex gap-2">
            {mode === 'vendors' ? (
              <>
                <Button variant="outline" onClick={() => setBulkOutreachOpen(true)} data-testid="open-bulk-outreach-btn">
                  <Megaphone className="w-4 h-4 mr-1" />Bulk Outreach
                </Button>
                <Button variant="outline" onClick={openAdd} data-testid="quick-add-vendor-btn"><Plus className="w-4 h-4 mr-1" />Quick Add</Button>
                <Button onClick={() => navigate('/pollen-dots/vendor/new?type=Hotel')} data-testid="new-hotel-btn"><Plus className="w-4 h-4 mr-1" />New Hotel (Full)</Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={() => { setBulkText(''); setBulkResult(null); setBulkOpen(true); }} data-testid="bulk-pin-btn">
                  <Upload className="w-4 h-4 mr-1" />Bulk Pin URLs
                </Button>
                <Button onClick={openAddLocation} data-testid="quick-add-location-btn"><Plus className="w-4 h-4 mr-1" />Add Location</Button>
              </>
            )}
          </div>
        </div>

        {/* Mode + type pills */}
        <div className="flex flex-wrap gap-2">
          {['', 'Hotel', 'Transport', 'Driver', 'Activity', 'Guide', 'Agent'].map(t => (
            <Button key={t || 'all'}
              variant={mode === 'vendors' && typeFilter === t ? 'default' : 'outline'}
              size="sm"
              onClick={() => { setMode('vendors'); setTypeFilter(t); }}
              data-testid={`db-pill-${t || 'all'}`}>
              {t || 'All Vendors'}
            </Button>
          ))}
          <Button
            variant={mode === 'locations' ? 'default' : 'outline'}
            size="sm"
            onClick={() => { setMode('locations'); setLocTypeFilter(''); }}
            data-testid="db-pill-locations">
            <MapPin className="w-3.5 h-3.5 mr-1" />Locations
          </Button>
        </div>

        {/* Populator-task scope banner — shows when arriving via task deep-link */}
        {taskMeta && (
          <Card className="border-amber-300 bg-amber-50" data-testid="populator-task-banner">
            <CardContent className="py-2.5 px-3 flex items-center gap-3 flex-wrap">
              <span className="text-[11px] uppercase tracking-wide text-amber-900 font-semibold">Active task</span>
              <span className="text-sm font-medium text-amber-950 truncate">
                {taskMeta.entity_type}s populator
              </span>
              <span className="text-xs text-amber-900 tabular-nums">
                {(taskMeta.done_count || 0)} / {(taskMeta.total_count || taskMeta.target || 0)} done
              </span>
              {taskMeta.due_at && (
                <span className="text-[11px] text-amber-900">
                  · due {new Date(taskMeta.due_at).toLocaleDateString()}
                </span>
              )}
              {taskMeta.notes && (
                <span className="text-[11px] text-amber-900 italic truncate">"{taskMeta.notes}"</span>
              )}
            </CardContent>
          </Card>
        )}

        {/* Filters */}
        <div className="flex flex-wrap gap-2">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input placeholder={mode === 'vendors' ? 'Search vendors...' : 'Search locations...'} className="pl-9" value={search} onChange={e => setSearch(e.target.value)} data-testid="vendor-search" />
          </div>
          {mode === 'vendors' && (
            <>
              <div className="relative min-w-[200px]">
                <LocationAutocomplete
                  value={locationFilter}
                  onChange={v => setLocationFilter(v)}
                  placeholder="Filter by location..."
                  className="h-10 text-sm"
                  testId="vendor-location-filter"
                />
              </div>
              <div className="flex gap-1 items-center">
                <Input type="number" placeholder="Min ₹" className="w-[90px]" value={priceMin} onChange={e => setPriceMin(e.target.value)} data-testid="vendor-price-min" />
                <span className="text-muted-foreground text-xs">–</span>
                <Input type="number" placeholder="Max ₹" className="w-[90px]" value={priceMax} onChange={e => setPriceMax(e.target.value)} data-testid="vendor-price-max" />
              </div>
              {(search || locationFilter || priceMin || priceMax || typeFilter) && (
                <Button variant="ghost" size="sm" onClick={() => { setSearch(''); setLocationFilter(''); setPriceMin(''); setPriceMax(''); setTypeFilter(''); }} data-testid="clear-filters">Clear</Button>
              )}
            </>
          )}
          {mode === 'locations' && (
            <>
              <div className="flex flex-wrap gap-1">
                <Button variant={locTypeFilter === '' ? 'default' : 'outline'} size="sm" onClick={() => setLocTypeFilter('')} data-testid="loc-type-all">All types</Button>
                {LOCATION_TYPES.map(lt => (
                  <Button key={lt} variant={locTypeFilter === lt ? 'default' : 'outline'} size="sm" onClick={() => setLocTypeFilter(lt)} data-testid={`loc-type-${lt}`}>
                    {lt === 'enroute_stop' ? 'Enroute Stop' : lt}
                  </Button>
                ))}
                <Button variant={locTypeFilter === '__needs_coords' ? 'default' : 'outline'} size="sm" onClick={() => setLocTypeFilter('__needs_coords')} data-testid="loc-type-needs-coords" className={locTypeFilter === '__needs_coords' ? '' : 'text-rose-600 border-rose-200 hover:bg-rose-50'}>
                  <AlertCircle className="w-3 h-3 mr-1" />Needs coords ({locations.filter(l => !l.verified).length})
                </Button>
              </div>
              {(search || locTypeFilter) && (
                <Button variant="ghost" size="sm" onClick={() => { setSearch(''); setLocTypeFilter(''); }}>Clear</Button>
              )}
            </>
          )}
        </div>

        {/* Locations header banner — one-glance summary of how many pins are
            still unverified, plus a shortcut to jump directly into the list of
            places that need Google Maps URLs. */}
        {mode === 'locations' && locations.length > 0 && (() => {
          const unverified = locations.filter(l => !l.verified).length;
          const verified = locations.length - unverified;
          if (unverified === 0) return null;
          return (
            <Card className="border-rose-200/80 bg-rose-50/60" data-testid="needs-coords-banner">
              <CardContent className="py-3 px-4 flex flex-wrap items-center gap-3 justify-between">
                <div className="flex items-center gap-3 text-sm">
                  <AlertCircle className="w-5 h-5 text-rose-500 shrink-0" />
                  <div>
                    <div className="font-semibold text-rose-800">
                      {unverified} of {locations.length} location{locations.length === 1 ? '' : 's'} still need exact coordinates
                    </div>
                    <div className="text-xs text-rose-700/80 leading-tight mt-0.5">
                      Unverified pins render as a soft ring on share maps. Paste the Google Maps URL for each to make them pop. {verified} already pinned.
                    </div>
                  </div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button size="sm" variant="outline" className="bg-white" onClick={() => setLocTypeFilter('__needs_coords')} data-testid="banner-show-unverified">
                    Show {unverified} unverified
                  </Button>
                  <Button size="sm" onClick={() => { setBulkText(''); setBulkResult(null); setBulkOpen(true); }} data-testid="banner-bulk-pin">
                    <Upload className="w-3.5 h-3.5 mr-1" />Bulk pin
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })()}

        {mode === 'locations' ? (
          filteredLocations.length === 0 ? (
            <Card><CardContent className="py-12 text-center text-muted-foreground">No locations found. Add one to plot travel history on a map.</CardContent></Card>
          ) : (
            <Card>
              <CardContent className="p-0">
                <div className="px-3 py-2.5 border-b bg-muted/40 text-[11px] text-muted-foreground">
                  Paste a Google Maps URL (or <span className="font-mono">25.28, 91.73</span>) against any place to verify its exact pin. Once saved it shows on customer &amp; network share maps.
                </div>
                <div className="divide-y max-h-[60vh] overflow-y-auto" data-testid="locations-list">
                  {filteredLocations.map(l => (
                    <SafeLocationPinRow
                      key={l.id}
                      loc={l}
                      onSaved={fetchData}
                      onEdit={() => handleLocEdit(l)}
                      onMerge={() => setMergeLocSource(l)}
                      onDelete={() => setLocDeleteId(l.id)}
                      authFetch={authFetch}
                      apiBase={POLLEN_API}
                    />
                  ))}
                </div>
              </CardContent>
            </Card>
          )
        ) : filtered.length === 0 ? (
          <Card><CardContent className="py-12 text-center text-muted-foreground">No vendors found</CardContent></Card>
        ) : (
          <>
            {/* Mobile Cards */}
            <div className="sm:hidden space-y-3 max-h-[70vh] overflow-y-auto pr-1">
              {filtered.map(v => (
                <Card key={v.id} className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate(`/pollen-dots/vendor/${v.id}`)}>
                  <CardContent className="p-4 space-y-2">
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-1.5 min-w-0 flex-1">
                        <span className="font-medium truncate">{v.name}</span>
                        {['Hotel','Activity','Guide','Driver'].includes(v.type) && (
                          isVendorPinned(v)
                            ? <span title="Pinned on share map" data-testid={`vendor-pinned-${v.id}`} className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-emerald-500 text-white shrink-0"><Check className="w-2.5 h-2.5" /></span>
                            : <span title="Not pinned — paste a Google Maps URL on the vendor page" data-testid={`vendor-unpinned-${v.id}`} className="inline-block w-2 h-2 rounded-full bg-amber-400 shrink-0" />
                        )}
                        {(v.initiatives || []).slice(0, 3).map(k => (
                          <span key={k} title={k} data-testid={`vendor-init-${v.id}-${k}`} className="inline-block w-1.5 h-1.5 rounded-full shrink-0" style={{ background: { 'smiles': '#f59e0b', 'cultural-exchange': '#c2410c', 'sustainability': '#84cc16' }[k] || '#94a3b8' }} />
                        ))}
                      </div>
                      <Badge className={typeColors[v.type]}>{v.type}</Badge>
                    </div>
                    {v.contact_name && <div className="text-xs text-muted-foreground">Contact: {v.contact_name}</div>}
                    {v.phone && <div className="text-xs text-muted-foreground"><Phone className="w-3 h-3 inline mr-1" />{v.phone}</div>}
                    {(v.location || v.address) && <div className="text-xs text-muted-foreground truncate"><MapPin className="w-3 h-3 inline mr-1" />{v.location || v.address}</div>}
                    {(v.rooms || []).length > 0 && (() => {
                      const prices = v.rooms.map(r => parseFloat(r.price) || 0).filter(p => p > 0);
                      if (!prices.length) return null;
                      return <div className="text-xs text-muted-foreground">Rooms: ₹{Math.min(...prices).toLocaleString('en-IN')} – ₹{Math.max(...prices).toLocaleString('en-IN')} ({v.rooms.length} cat)</div>;
                    })()}
                    <div className="flex justify-between pt-1 border-t text-xs">
                      <span>Business: <strong>{(v.total_business || 0).toLocaleString('en-IN')}</strong></span>
                      <span className="text-emerald-600">Paid: {(v.total_paid || 0).toLocaleString('en-IN')}</span>
                      <span className="text-red-600 font-medium">Due: {(v.outstanding || 0).toLocaleString('en-IN')}</span>
                    </div>
                    {/* Inline data-quality taggers — fix gaps without
                        leaving the directory. Hotels also see the
                        accommodation-type chips. */}
                    <div className="flex flex-wrap items-center gap-1.5 pt-1" onClick={e => e.stopPropagation()}>
                      {v.type === 'Hotel' && <AccommodationTypeTagger vendor={v} />}
                      <ContactTagger vendor={v} />
                    </div>
                    <div className="flex justify-end gap-1 pt-1" onClick={e => e.stopPropagation()}>
                      <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => handleEdit(v)}><Pencil className="w-3 h-3 mr-1" />Edit</Button>
                      <Button variant="outline" size="sm" className="h-7 text-xs text-amber-600" title="Merge duplicate" onClick={() => setMergeSource(v)} data-testid={`merge-vendor-${v.id}`}><GitMerge className="w-3 h-3 mr-1" />Merge</Button>
                      <Button variant="outline" size="sm" className="h-7 text-xs text-destructive" onClick={() => setDeleteId(v.id)}><Trash2 className="w-3 h-3 mr-1" />Delete</Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            {/* Desktop Table */}
            <div className="hidden sm:block overflow-x-auto max-h-[70vh] overflow-y-auto">
              <Table>
              <TableHeader><TableRow>
                <TableHead>Name</TableHead><TableHead>Type</TableHead>
                <TableHead>Location</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead className="text-right">Price Range</TableHead>
                <TableHead className="text-right">Total Business</TableHead>
                <TableHead className="text-right">Paid</TableHead>
                <TableHead className="text-right">Outstanding</TableHead><TableHead className="text-right">Actions</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {filtered.map(v => {
                  const prices = (v.rooms || []).map(r => parseFloat(r.price) || 0).filter(p => p > 0);
                  const priceRange = prices.length ? (prices.length === 1 ? prices[0].toLocaleString('en-IN') : `${Math.min(...prices).toLocaleString('en-IN')} – ${Math.max(...prices).toLocaleString('en-IN')}`) : '-';
                  return (
                  <TableRow key={v.id} className="cursor-pointer hover:bg-muted/50" onClick={() => navigate(`/pollen-dots/vendor/${v.id}`)} data-testid={`vendor-row-${v.id}`}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-1.5">
                        <span className="truncate">{v.name}</span>
                        {['Hotel','Activity','Guide','Driver'].includes(v.type) && (
                          isVendorPinned(v)
                            ? <span title="Pinned on share map" data-testid={`vendor-pinned-row-${v.id}`} className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-emerald-500 text-white shrink-0"><Check className="w-2.5 h-2.5" /></span>
                            : <span title="Not pinned — paste a Google Maps URL on the vendor page" data-testid={`vendor-unpinned-row-${v.id}`} className="inline-block w-2 h-2 rounded-full bg-amber-400 shrink-0" />
                        )}
                        {(v.initiatives || []).slice(0, 3).map(k => (
                          <span key={k} title={k} className="inline-block w-1.5 h-1.5 rounded-full shrink-0" style={{ background: { 'smiles': '#f59e0b', 'cultural-exchange': '#c2410c', 'sustainability': '#84cc16' }[k] || '#94a3b8' }} />
                        ))}
                      </div>
                    </TableCell>
                    <TableCell><Badge className={typeColors[v.type]}>{v.type}</Badge></TableCell>
                    <TableCell className="text-muted-foreground text-xs max-w-[160px] truncate">{v.location || v.address || '-'}</TableCell>
                    <TableCell className="text-muted-foreground">{v.contact_name || '-'}</TableCell>
                    <TableCell className="text-muted-foreground" onClick={e => e.stopPropagation()}>
                      <div className="flex flex-col gap-1 min-w-[120px]">
                        <ContactTagger vendor={v} />
                        {v.type === 'Hotel' && <AccommodationTypeTagger vendor={v} />}
                      </div>
                    </TableCell>
                    <TableCell className="text-right text-xs">{priceRange}</TableCell>
                    <TableCell className="text-right">{(v.total_business || 0).toLocaleString('en-IN')}</TableCell>
                    <TableCell className="text-right text-emerald-600">{(v.total_paid || 0).toLocaleString('en-IN')}</TableCell>
                    <TableCell className="text-right font-medium text-red-600">{(v.outstanding || 0).toLocaleString('en-IN')}</TableCell>
                    <TableCell className="text-right" onClick={e => e.stopPropagation()}>
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(v)}><Pencil className="w-4 h-4" /></Button>
                      <Button variant="ghost" size="sm" className="text-amber-600" title="Merge duplicate" onClick={() => setMergeSource(v)} data-testid={`merge-vendor-row-${v.id}`}><GitMerge className="w-4 h-4" /></Button>
                      <Button variant="ghost" size="sm" className="text-destructive" onClick={() => setDeleteId(v.id)}><Trash2 className="w-4 h-4" /></Button>
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
          </>
        )}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle>{editMode ? 'Edit' : 'Add'} Vendor</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-3 mt-2">
              <div><Label>Name *</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} required data-testid="input-vendor-name" /></div>
              <div><Label>Type</Label>
                <Select value={form.type} onValueChange={v => setForm({...form, type: v})}>
                  <SelectTrigger data-testid="select-vendor-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{['Hotel', 'Transport', 'Driver', 'Activity', 'Guide', 'Agent'].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><Label>Contact Name</Label><Input value={form.contact_name} onChange={e => setForm({...form, contact_name: e.target.value})} /></div>
              <div><Label>Phone <span className="text-[10px] text-muted-foreground">(comma-separated for multiple)</span></Label><Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} placeholder="+91 9317752915, +91 9805541209" /></div>
              <div><Label>Email <span className="text-[10px] text-muted-foreground">(comma-separated for multiple)</span></Label><Input type="text" inputMode="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} placeholder="ops@hotel.com, reservations@hotel.com" /></div>
              <div><Label>Location (city / area)</Label>
                <LocationAutocomplete value={form.location} type="city" className="h-10 text-sm"
                  onChange={v => setForm({ ...form, location: v })} placeholder="e.g., Shillong, Paro" testId="quick-add-vendor-location" />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                <Button type="submit" disabled={submitting} data-testid="submit-vendor">{submitting && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}{editMode ? 'Update' : 'Add'}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        <ConfirmDeleteDialog open={!!deleteId} onClose={() => setDeleteId(null)} onConfirm={() => handleDelete(deleteId)} title="Delete Vendor" description="This will permanently delete this vendor. Are you sure?" />

        {/* Location Dialog */}
        <Dialog open={locDialogOpen} onOpenChange={setLocDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle>{locEditMode ? 'Edit' : 'Add'} Location</DialogTitle></DialogHeader>
            <form onSubmit={handleLocSubmit} className="space-y-3 mt-2">
              <div><Label>Name *</Label><Input value={locForm.name} onChange={e => setLocForm({ ...locForm, name: e.target.value })} required data-testid="input-location-name" /></div>
              <div><Label>Type</Label>
                <Select value={locForm.type} onValueChange={v => setLocForm({ ...locForm, type: v })}>
                  <SelectTrigger data-testid="select-location-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{LOCATION_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select></div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label>Country</Label><Input value={locForm.country} onChange={e => setLocForm({ ...locForm, country: e.target.value })} /></div>
                <div><Label>State</Label><Input value={locForm.state} onChange={e => setLocForm({ ...locForm, state: e.target.value })} /></div>
              </div>
              <div><Label>Region</Label><Input value={locForm.region} onChange={e => setLocForm({ ...locForm, region: e.target.value })} placeholder="e.g., Himalayas, Western Ghats" /></div>
              <div><Label>Notes</Label><Input value={locForm.notes} onChange={e => setLocForm({ ...locForm, notes: e.target.value })} /></div>
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setLocDialogOpen(false)}>Cancel</Button>
                <Button type="submit" disabled={submitting} data-testid="submit-location">{submitting && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}{locEditMode ? 'Update' : 'Add'}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        <ConfirmDeleteDialog open={!!locDeleteId} onClose={() => setLocDeleteId(null)} onConfirm={() => handleLocDelete(locDeleteId)} title="Delete Location" description="This will permanently delete this location. Tours referencing it will still keep their text. Are you sure?" />

        {/* Merge dialogs — shared between Vendors + Locations tabs */}
        <MergeIntoDialog
          open={!!mergeSource}
          onOpenChange={(v) => !v && setMergeSource(null)}
          sourceItem={mergeSource}
          candidates={vendors}
          endpoint="/pollen/vendors/merge"
          entityLabel="vendor"
          itemMeta={(v) => [v.type, v.location, v.phone].filter(Boolean).join(' \u00b7 ')}
          allowCrossType
          onMerged={fetchData}
        />
        <MergeIntoDialog
          open={!!mergeLocSource}
          onOpenChange={(v) => !v && setMergeLocSource(null)}
          sourceItem={mergeLocSource}
          candidates={locations}
          endpoint="/pollen/locations/merge"
          entityLabel="location"
          itemMeta={(l) => [l.type, l.country, l.state].filter(Boolean).join(' \u00b7 ')}
          onMerged={fetchData}
        />

        {/* Bulk Pin URLs dialog */}
        <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
          <DialogContent className="max-w-xl">
            <DialogHeader><DialogTitle>Bulk pin Google Maps URLs</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">
                Paste one place per line, in the format <code className="text-[11px] bg-muted px-1 rounded">Name = URL</code>.
                Names are matched case-insensitively against existing locations in this database.
              </p>
              <Textarea
                rows={8}
                placeholder={`Cherrapunjee = https://maps.google.com/?q=25.28,91.73\nGoa = https://www.google.com/maps/place/Goa/@15.30,74.09,9z\nThimpu = https://maps.google.com/...`}
                value={bulkText}
                onChange={e => setBulkText(e.target.value)}
                className="font-mono text-xs"
                data-testid="bulk-pin-textarea"
              />
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setBulkOpen(false)}>Close</Button>
                <Button onClick={runBulkPin} disabled={bulkBusy || !bulkText.trim()} data-testid="bulk-pin-submit">
                  {bulkBusy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
                  Parse &amp; pin
                </Button>
              </div>
              {bulkResult && (
                <div className="border-t pt-3 space-y-2 max-h-64 overflow-y-auto" data-testid="bulk-pin-result">
                  {bulkResult.pinned?.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-teal-700 mb-1">Pinned ({bulkResult.pinned.length})</p>
                      <ul className="text-[11px] text-muted-foreground space-y-0.5">
                        {bulkResult.pinned.map((p, i) => (
                          <li key={i} className="flex items-center gap-2">
                            <Check className="w-3 h-3 text-teal-600 shrink-0" />
                            <span className="font-medium text-foreground">{p.name}</span>
                            <span className="font-mono">{p.lat.toFixed(4)}, {p.lng.toFixed(4)}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {bulkResult.skipped_unknown?.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-amber-700 mb-1">Not in database ({bulkResult.skipped_unknown.length})</p>
                      <p className="text-[11px] text-muted-foreground">{bulkResult.skipped_unknown.join(', ')}</p>
                      <p className="text-[10px] text-muted-foreground mt-0.5 italic">Add these as locations first, then re-run the bulk pin.</p>
                    </div>
                  )}
                  {bulkResult.skipped_invalid?.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-rose-700 mb-1">Invalid ({bulkResult.skipped_invalid.length})</p>
                      <ul className="text-[11px] text-muted-foreground">
                        {bulkResult.skipped_invalid.map((s, i) => (
                          <li key={i}><span className="font-medium text-foreground">{s.name}</span> &mdash; {s.reason}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Stage D — Bulk Outreach (no query). Pre-fills with current type / location filters. */}
        <BulkOutreachSheet
          open={bulkOutreachOpen}
          onOpenChange={setBulkOutreachOpen}
          defaultType={typeFilter || ''}
          defaultLocation={locationFilter || ''}
        />
      </div>
    </Layout>

  );
};

export default PollenDotsVendors;

/* ---------- Inline location pin row ----------
 * Shows a location in the unified DB + an inline URL input so an admin can
 * set exact coords from a pasted Google Maps URL without opening the row.
 * Once saved, the row flips to "Verified ✓" and the dot lights up on the
 * share maps. Reused across the Database → Locations view.
 */
const LocationPinRow = ({ loc, onSaved, onEdit, onDelete, onMerge, authFetch, apiBase }) => {
  const [url, setUrl] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [repinning, setRepinning] = React.useState(false);
  // Coerce to numbers — legacy docs occasionally have lat/lng as strings,
  // which crashes .toFixed() and blanks the whole list. Treat anything
  // non-numeric as "no coords" and let the operator re-pin.
  const lat = typeof loc.lat === 'number' && !Number.isNaN(loc.lat) ? loc.lat : null;
  const lng = typeof loc.lng === 'number' && !Number.isNaN(loc.lng) ? loc.lng : null;
  const hasCoords = lat != null && lng != null;
  const isVerified = !!loc.verified && hasCoords;
  // Notes editor — enroute stops carry canonical first-write-wins notes
  // that operators wanted to be able to amend from the Locations DB
  // page (the only spot allowed to override the original tour's notes).
  const isStop = loc.type === 'enroute_stop';
  const [notes, setNotes] = React.useState(loc.notes || '');
  const [editingNotes, setEditingNotes] = React.useState(false);
  const [savingNotes, setSavingNotes] = React.useState(false);
  React.useEffect(() => { setNotes(loc.notes || ''); }, [loc.notes]);

  const saveNotes = async () => {
    setSavingNotes(true);
    try {
      const res = await authFetch(`${apiBase}/locations/${loc.id}/set-notes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ notes: notes.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Could not update notes');
      toast.success('Canonical notes updated');
      setEditingNotes(false);
      onSaved && onSaved();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSavingNotes(false);
    }
  };

  const save = async (override) => {
    const raw = (override ?? url).trim();
    if (!raw) return;
    setBusy(true);
    try {
      const res = await authFetch(`${apiBase}/locations/${loc.id}/set-coords`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ google_maps_url: raw }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Could not set coordinates');
      toast.success(`${loc.name} pinned ✓  (${data.lat.toFixed(4)}, ${data.lng.toFixed(4)})`);
      setUrl(''); setRepinning(false);
      onSaved && onSaved();
    } catch (e) {
      toast.error(e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="p-3 flex flex-col gap-2" data-testid={`location-row-${loc.id}`}>
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
      {/* Name + meta */}
      <div className="flex items-start gap-2 sm:w-64 shrink-0">
        <MapPin className={`w-4 h-4 mt-0.5 shrink-0 ${isVerified ? 'text-teal-600' : hasCoords ? 'text-amber-500' : 'text-rose-500'}`} />
        <div className="min-w-0">
          <p className="text-sm font-medium truncate">{loc.name}</p>
          <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
            <Badge className={`${locTypeColors[loc.type] || 'bg-gray-100 text-gray-800'} text-[10px]`}>{loc.type === 'enroute_stop' ? 'Enroute Stop' : loc.type}</Badge>
            {[loc.country, loc.state, loc.region].filter(Boolean).length > 0 && (
              <span className="text-[10px] text-muted-foreground">{[loc.country, loc.state, loc.region].filter(Boolean).join(' · ')}</span>
            )}
            <span className="text-[10px] text-muted-foreground">Used in {loc.usage_count || 0}</span>
          </div>
        </div>
      </div>

      {/* Status + URL input */}
      <div className="flex-1 flex items-center gap-2 min-w-0">
        {isVerified && !repinning ? (
          <div className="flex items-center gap-2 text-[11px] text-teal-700 bg-teal-50 border border-teal-200 rounded px-2 py-1 font-medium">
            <Check className="w-3.5 h-3.5" />
            Verified
            <span className="font-mono text-teal-600/80">{lat.toFixed(4)}, {lng.toFixed(4)}</span>
          </div>
        ) : (
          <>
            {!isVerified && hasCoords && (
              <span className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5 font-mono">
                auto {lat.toFixed(2)}, {lng.toFixed(2)}
              </span>
            )}
            <Input
              placeholder='Paste Google Maps URL or "25.28, 91.73"'
              value={url}
              onChange={e => setUrl(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') save(); }}
              className="h-8 text-xs flex-1 min-w-0"
              data-testid={`location-url-input-${loc.id}`}
            />
            <Button size="sm" onClick={() => save()} disabled={busy || !url.trim()} className="h-8 text-xs" data-testid={`location-save-${loc.id}`}>
              {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Check className="w-3 h-3 mr-1" />Pin</>}
            </Button>
            {repinning && (
              <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={() => { setRepinning(false); setUrl(''); }} data-testid={`loc-repin-cancel-${loc.id}`}>Cancel</Button>
            )}
          </>
        )}
        {hasCoords && !repinning && (
          <a
            href={`https://www.google.com/maps/?q=${lat},${lng}`}
            target="_blank"
            rel="noopener noreferrer"
            title="Open in Google Maps"
            className="text-muted-foreground hover:text-primary p-1 shrink-0"
            data-testid={`location-open-${loc.id}`}
          >
            <ExternalLink className="w-3.5 h-3.5" />
          </a>
        )}
      </div>

      {/* Row actions */}
      <div className="flex items-center gap-1 shrink-0">
        {isVerified && !repinning && (
          <Button variant="ghost" size="sm" onClick={() => setRepinning(true)}
            className="h-8 text-[11px] text-muted-foreground hover:text-primary" title="Re-pin with a new URL" data-testid={`loc-repin-${loc.id}`}>
            Re-pin
          </Button>
        )}
        <Button variant="ghost" size="sm" onClick={onEdit} data-testid={`loc-edit-${loc.id}`}><Pencil className="w-4 h-4" /></Button>
        {onMerge && (
          <Button variant="ghost" size="sm" className="text-amber-600" title="Merge duplicate location" onClick={onMerge} data-testid={`loc-merge-${loc.id}`}><GitMerge className="w-4 h-4" /></Button>
        )}
        <Button variant="ghost" size="sm" className="text-destructive" onClick={onDelete} data-testid={`loc-delete-${loc.id}`}><Trash2 className="w-4 h-4" /></Button>
      </div>
      </div>
      {/* Canonical first-write-wins notes — only enroute stops carry
          them. Click the line to amend in place; saving here updates
          the canonical row, which is what autocomplete will hydrate
          into all future tours that pick this stop. */}
      {isStop && (
        editingNotes ? (
          <div className="flex items-start gap-2 sm:pl-6">
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              rows={2}
              className="flex-1 text-xs px-2 py-1.5 border rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-amber-400"
              placeholder="Canonical notes (autocomplete will hydrate these into future tours)"
              data-testid={`location-notes-input-${loc.id}`}
            />
            <Button size="sm" onClick={saveNotes} disabled={savingNotes} className="h-7 text-xs px-2" data-testid={`location-notes-save-${loc.id}`}>
              {savingNotes ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Save'}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => { setNotes(loc.notes || ''); setEditingNotes(false); }} className="h-7 text-xs px-2">Cancel</Button>
          </div>
        ) : (
          <button
            type="button"
            className="sm:pl-6 text-left text-[11px] text-muted-foreground italic hover:text-foreground transition-colors flex items-start gap-1.5"
            onClick={() => setEditingNotes(true)}
            data-testid={`location-notes-edit-${loc.id}`}
            title="Click to edit canonical notes"
          >
            <Sparkles className="w-3 h-3 mt-0.5 shrink-0 text-amber-500" />
            <span className="line-clamp-2">{(loc.notes || '').trim() || '+ Add canonical notes (shown in tours when this stop is picked)'}</span>
          </button>
        )
      )}
    </div>
  );
};


// Error boundary so a single malformed pollen_locations document
// (e.g. legacy lat stored as a string) can NEVER blank the entire
// Database → Locations view. The bad row degrades to an inline error
// chip; every other row keeps rendering.
class SafeLocationPinRow extends React.Component {
  constructor(p) { super(p); this.state = { err: null }; }
  static getDerivedStateFromError(err) { return { err }; }
  componentDidCatch(err) { console.error('LocationPinRow crashed:', err); }
  render() {
    if (this.state.err) {
      const l = this.props.loc || {};
      return (
        <div className="p-3 flex items-center gap-2 text-xs text-rose-600" data-testid={`location-row-error-${l.id || 'unknown'}`}>
          <MapPin className="w-4 h-4 shrink-0 text-rose-500" />
          <span className="truncate">
            <strong>{l.name || '(unnamed)'}</strong> — couldn't render this row. Edit it or paste a Google Maps URL elsewhere to repair the record.
          </span>
        </div>
      );
    }
    return <LocationPinRow {...this.props} />;
  }
}
